import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const API_BASE = "https://dashboard.jyotirvani.com";

const normalizeText = (value, fallback = "Not available") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (Array.isArray(value))
    return value.length ? normalizeText(value[0], fallback) : fallback;

  if (typeof value === "object") {
    return (
      value.current_status ||
      value.shipment_status ||
      value.order_status ||
      value.status ||
      value.name ||
      value.title ||
      value.message ||
      fallback
    );
  }

  return String(value);
};

const getTrackingObject = (trackingData) => {
  if (!trackingData) return {};

  const data =
    trackingData?.data?.tracking_data ||
    trackingData?.tracking_data ||
    trackingData?.data?.data ||
    trackingData?.data?.response ||
    trackingData?.response ||
    trackingData?.data ||
    trackingData;

  if (Array.isArray(data)) return data[0] || {};
  return data || {};
};

const getStatusStage = (statusText = "", hasAwb = false) => {
  const status = String(statusText).toLowerCase();

  if (status.includes("cancel")) return 0;
  if (status.includes("delivered")) return 5;
  if (status.includes("out for delivery") || status.includes("ofd")) return 4;

  if (
    status.includes("transit") ||
    status.includes("shipped") ||
    status.includes("picked") ||
    status.includes("manifest") ||
    status.includes("dispatched")
  ) {
    return 3;
  }

  if (hasAwb) return 3;

  if (
    status.includes("processing") ||
    status.includes("packed") ||
    status.includes("ready") ||
    status.includes("created") ||
    status.includes("placed") ||
    status.includes("pending") ||
    status.includes("new")
  ) {
    return 2;
  }

  return 1;
};

const TrackOrder = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const paymentStatus = searchParams.get("payment");
  const isPaymentSuccess = paymentStatus === "success";

  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trackingError, setTrackingError] = useState("");

  const fetchTracking = useCallback(async () => {
    if (!orderId) return;

    try {
      setLoading(true);
      setTrackingError("");

      const response = await fetch(`${API_BASE}/api/track-order/${orderId}`, {
        headers: { Accept: "application/json" },
      });

      const text = await response.text();
      console.log("RAW TRACKING RESPONSE:", text);

      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Tracking API JSON return korche na");
      }

      if (!response.ok) {
        throw new Error(data?.message || "Tracking API failed");
      }

      setTrackingData(data);
    } catch (err) {
      console.error("Tracking Error:", err);
      setTrackingError(err.message || "Tracking details not available yet");
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (!orderId) {
      navigate("/cart");
      return;
    }

    fetchTracking();
    const interval = setInterval(fetchTracking, 30000);

    return () => clearInterval(interval);
  }, [orderId, navigate, fetchTracking]);

  const track = useMemo(() => getTrackingObject(trackingData), [trackingData]);

  const rawStatus = normalizeText(
    track?.current_status ||
      track?.shipment_status ||
      track?.order_status ||
      track?.status,
    "",
  );

  const awbValue =
    track?.awb_code ||
    track?.awb ||
    track?.awb_number ||
    track?.tracking_number ||
    track?.waybill ||
    "";

  const hasAwb = Boolean(awbValue);
  const isCancelled = String(rawStatus).toLowerCase().includes("cancel");

const safeRawStatus = String(rawStatus || "");

const statusText = isCancelled
  ? "Order Cancelled"
  : safeRawStatus && safeRawStatus.toLowerCase() !== "failed"
    ? safeRawStatus
    : isPaymentSuccess
      ? "Shipment Pending"
      : "Order Tracking Pending";

  const trackingNumber = normalizeText(
    awbValue,
    isPaymentSuccess ? "AWB not assigned yet" : "Not assigned yet",
  );

  const courierName = normalizeText(
    track?.courier_name ||
      track?.courier ||
      track?.carrier ||
      track?.carrier_name ||
      track?.shipping_provider,
    isPaymentSuccess ? "Courier not assigned yet" : "Not assigned yet",
  );

  const expectedDate = normalizeText(
    track?.expected_delivery_date ||
      track?.edd ||
      track?.etd ||
      track?.delivery_date,
    isPaymentSuccess ? "Will update after shipment" : "Not available",
  );

  const currentStage = isCancelled ? 0 : getStatusStage(statusText, hasAwb);

  const steps = [
    {
      stage: 1,
      title: "Order Placed",
      desc: "Your order has been created successfully.",
      icon: "✅",
    },
    {
      stage: 2,
      title: "Shipment Processing",
      desc: "Your shipment is pending and will be processed soon.",
      icon: "📦",
    },
    {
      stage: 3,
      title: "In Transit",
      desc: "Your order has been shipped and is on the way.",
      icon: "🚚",
    },
    {
      stage: 4,
      title: "Out For Delivery",
      desc: "Delivery partner is near your location.",
      icon: "📍",
    },
    {
      stage: 5,
      title: "Delivered",
      desc: "Order delivered successfully.",
      icon: "🎉",
    },
  ];

  return (
    <>
      <style>{`
        @media (max-width: 900px) {
          .track-main-grid-fix {
            grid-template-columns: 1fr !important;
          }
        }

        @media (max-width: 600px) {
          .track-page-fix {
            padding: 14px 10px 28px !important;
          }

          .track-hero-fix {
            padding: 24px 18px !important;
            border-radius: 24px !important;
          }

          .track-title-fix {
            font-size: 28px !important;
            line-height: 1.2 !important;
          }

          .track-hero-text-fix {
            font-size: 14px !important;
            line-height: 1.5 !important;
          }

          .track-top-steps-fix {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 10px !important;
          }

          .track-top-step-fix {
            min-height: 125px !important;
            padding: 12px !important;
            border-radius: 18px !important;
          }

          .track-step-icon-fix {
            font-size: 24px !important;
          }

          .track-step-title-fix {
            font-size: 14px !important;
            line-height: 1.3 !important;
          }

          .track-step-text-fix {
            font-size: 12px !important;
            line-height: 1.4 !important;
          }

          .track-card-fix,
          .track-timeline-card-fix {
            padding: 18px !important;
            border-radius: 20px !important;
          }

          .track-info-value-fix {
            font-size: 14px !important;
          }

          .track-timeline-wrap-fix {
            margin-top: 18px !important;
          }

          .track-step-wrap-fix {
            grid-template-columns: 38px 1fr !important;
            gap: 12px !important;
            padding-bottom: 22px !important;
          }

          .track-line-fix {
            left: 18px !important;
            top: 38px !important;
            height: calc(100% - 38px) !important;
          }

          .track-icon-circle-fix {
            width: 38px !important;
            height: 38px !important;
            font-size: 18px !important;
          }

          .track-step-box-fix {
            padding: 13px !important;
            border-radius: 16px !important;
          }

          .track-step-box-fix h3 {
            font-size: 14px !important;
            line-height: 1.35 !important;
          }

          .track-current-badge-fix {
            display: inline-block !important;
            margin-left: 0 !important;
            margin-top: 6px !important;
          }

          .track-info-box-fix {
            font-size: 13px !important;
            padding: 14px !important;
          }

          .track-support-title-fix {
            font-size: 16px !important;
          }
        }

        @media (max-width: 380px) {
          .track-top-steps-fix {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <div style={pageStyle} className="track-page-fix">
        <div style={containerStyle}>
          <div style={heroStyle} className="track-hero-fix">
            <h1 style={heroTitleStyle} className="track-title-fix">
              Track Your Order
            </h1>
            <p style={heroTextStyle} className="track-hero-text-fix">
              Live delivery status for your JyotirVani order
            </p>
          </div>

          <div style={topStepsStyle} className="track-top-steps-fix">
            {[
              ["🛒", "Cart", "Completed"],
              ["💳", "Checkout & Payment", "Completed"],
              ["🚚📍", "Delivery & Track", "Track your JyotirVani order live"],
            ].map((step, index) => (
              <div
                key={index}
                className="track-top-step-fix"
                style={{
                  ...topStepCardStyle,
                  border:
                    index === 2 ? "2px solid #7c3aed" : "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{ fontSize: "26px" }}
                  className="track-step-icon-fix"
                >
                  {step[0]}
                </div>
                <h3
                  className="track-step-title-fix"
                  style={{ margin: "8px 0 4px", fontSize: "16px" }}
                >
                  {step[1]}
                </h3>
                <p
                  className="track-step-text-fix"
                  style={{ margin: 0, color: "#6b7280", fontSize: "13px" }}
                >
                  {step[2]}
                </p>
              </div>
            ))}
          </div>

          <div style={mainGridStyle} className="track-main-grid-fix">
            <div style={cardStyle} className="track-card-fix">
              <h2 style={{ marginTop: 0 }}>Order Details</h2>

              <InfoRow label="Order ID" value={orderId} />
              <InfoRow label="Tracking No" value={trackingNumber} />
              <InfoRow label="Courier" value={courierName} />
              <InfoRow label="Current Status" value={statusText} />
              <InfoRow label="Expected Delivery" value={expectedDate} />

              {loading && (
                <p style={loadingTextStyle}>Loading tracking details...</p>
              )}

              {trackingError && (
                <p style={warningTextStyle}>
                  Tracking details will update after courier assignment.
                </p>
              )}

              {isCancelled && (
                <p style={cancelTextStyle}>
                  This order has been cancelled. Please contact support for
                  help.
                </p>
              )}

              <button onClick={fetchTracking} style={refreshButtonStyle}>
                Refresh Status
              </button>

              <button
                onClick={() => navigate(`/OrderCreated/${orderId}`)}
                style={outlineButtonStyle}
              >
                Order Summary
              </button>

              {/* <button onClick={() => navigate("/")} style={outlineButtonStyle}>
                Back To Home
              </button> */}
            </div>

            <div style={timelineCardStyle} className="track-timeline-card-fix">
              <h2 style={{ marginTop: 0 }}>Delivery Timeline</h2>

              <div
                style={{
                  ...statusBadgeStyle,
                  background: isCancelled
                    ? "#fee2e2"
                    : currentStage >= 2
                      ? "#dcfce7"
                      : "#ede9fe",
                  color: isCancelled
                    ? "#dc2626"
                    : currentStage >= 2
                      ? "#166534"
                      : "#5b21b6",
                }}
              >
                {statusText}
              </div>

              <div
                style={{ marginTop: "25px" }}
                className="track-timeline-wrap-fix"
              >
                {steps.map((step, index) => {
                  const current = currentStage === step.stage;

                  return (
                    <div
                      key={step.stage}
                      style={stepWrapStyle}
                      className="track-step-wrap-fix"
                    >
                      {index !== steps.length - 1 && (
                        <div
                          className="track-line-fix"
                          style={{
                            ...lineStyle,
                            background:
                              currentStage > step.stage ? "#22c55e" : "#e5e7eb",
                          }}
                        />
                      )}

                      <div
                        className="track-icon-circle-fix"
                        style={{
                          ...iconCircleStyle,
                          background:
                            currentStage >= step.stage ? "#dcfce7" : "#f3f4f6",
                          color:
                            currentStage >= step.stage ? "#16a34a" : "#9ca3af",
                          border: current
                            ? "3px solid #f97316"
                            : currentStage >= step.stage
                              ? "2px solid #22c55e"
                              : "2px solid #e5e7eb",
                        }}
                      >
                        {step.icon}
                      </div>

                      <div
                        className="track-step-box-fix"
                        style={{
                          ...stepBoxStyle,
                          background:
                            currentStage >= step.stage ? "#f0fdf4" : "#f9fafb",
                          border: current
                            ? "1px solid #fdba74"
                            : currentStage >= step.stage
                              ? "1px solid #bbf7d0"
                              : "1px solid #e5e7eb",
                        }}
                      >
                        <h3
                          style={{
                            margin: "0 0 6px",
                            color:
                              currentStage >= step.stage
                                ? "#166534"
                                : "#374151",
                          }}
                        >
                          {step.title}

                          {current && (
                            <span
                              style={currentBadgeStyle}
                              className="track-current-badge-fix"
                            >
                              Current
                            </span>
                          )}
                        </h3>

                        <p style={stepDescStyle}>{step.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={infoBoxStyle} className="track-info-box-fix">
                Payment, product, delivery, or tracking related issues? Please
                contact our support team below. We are always ready to help you
                💜
                <div style={supportDividerStyle}>
                  <h3
                    style={supportTitleStyle}
                    className="track-support-title-fix"
                  >
                    Help With Us 💜
                  </h3>

                  <a href="mailto:support@jyotirvani.com" style={mailLinkStyle}>
                    📧 support@jyotirvani.com
                  </a>

                  <a href="tel:+919616339090" style={phoneLinkStyle}>
                    📞 +91 96163 39090
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const InfoRow = ({ label, value }) => (
  <div style={infoRowStyle}>
    <p style={infoLabelStyle}>{label}</p>
    <h3 style={infoValueStyle} className="track-info-value-fix">
      {normalizeText(value)}
    </h3>
  </div>
);

const pageStyle = {
  minHeight: "100vh",
  background:
    "radial-gradient(circle at top left, #fff7ed 0, #ffffff 38%, #f5f3ff 100%)",
  padding: "25px 16px 35px",
  fontFamily: "Arial, sans-serif",
};

const containerStyle = {
  maxWidth: "1150px",
  margin: "0 auto",
};

const topStepsStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: "18px",
  marginBottom: "24px",
};

const topStepCardStyle = {
  background: "#fff",
  borderRadius: "20px",
  padding: "18px",
  boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
};

const heroStyle = {
  background: "linear-gradient(135deg, #4c1d95, #7c3aed, #f97316)",
  color: "#fff",
  padding: "32px 24px",
  borderRadius: "28px",
  boxShadow: "0 22px 45px rgba(76,29,149,0.25)",
  marginBottom: "24px",
};

const heroTitleStyle = {
  margin: 0,
  fontSize: "34px",
  fontWeight: "800",
};

const heroTextStyle = {
  margin: "10px 0 0",
  opacity: 0.9,
};

const mainGridStyle = {
  display: "grid",
  gridTemplateColumns: "360px minmax(0, 1fr)",
  gap: "24px",
  alignItems: "start",
};

const cardStyle = {
  background: "#fff",
  borderRadius: "24px",
  padding: "24px",
  boxShadow: "0 18px 45px rgba(15,23,42,0.08)",
};

const timelineCardStyle = {
  background: "#fff",
  borderRadius: "24px",
  padding: "28px",
  boxShadow: "0 18px 45px rgba(15,23,42,0.08)",
};

const infoRowStyle = {
  padding: "13px 0",
  borderBottom: "1px solid #eee",
};

const infoLabelStyle = {
  margin: "0 0 5px",
  color: "#6b7280",
  fontSize: "14px",
};

const infoValueStyle = {
  margin: 0,
  color: "#111827",
  fontSize: "16px",
  wordBreak: "break-word",
};

const loadingTextStyle = {
  color: "#7c3aed",
  fontWeight: "700",
};

const warningTextStyle = {
  color: "#ca8a04",
  fontWeight: "700",
  lineHeight: "1.5",
};

const cancelTextStyle = {
  color: "#dc2626",
  fontWeight: "700",
  lineHeight: "1.5",
};

const refreshButtonStyle = {
  width: "100%",
  marginTop: "18px",
  border: "none",
  borderRadius: "999px",
  padding: "14px",
  background: "linear-gradient(135deg, #16a34a, #22c55e)",
  color: "#fff",
  fontWeight: "800",
  cursor: "pointer",
};

const outlineButtonStyle = {
  width: "100%",
  marginTop: "12px",
  border: "2px solid #7c3aed",
  borderRadius: "999px",
  padding: "13px",
  background: "#fff",
  color: "#7c3aed",
  fontWeight: "800",
  cursor: "pointer",
};

const statusBadgeStyle = {
  display: "inline-block",
  marginTop: "6px",
  padding: "8px 14px",
  borderRadius: "999px",
  fontWeight: "800",
  fontSize: "14px",
};

const stepWrapStyle = {
  display: "grid",
  gridTemplateColumns: "46px 1fr",
  gap: "16px",
  position: "relative",
  paddingBottom: "28px",
};

const lineStyle = {
  position: "absolute",
  left: "22px",
  top: "46px",
  width: "3px",
  height: "calc(100% - 46px)",
  borderRadius: "10px",
};

const iconCircleStyle = {
  width: "46px",
  height: "46px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "22px",
  zIndex: 2,
};

const stepBoxStyle = {
  borderRadius: "18px",
  padding: "16px",
};

const currentBadgeStyle = {
  marginLeft: "10px",
  fontSize: "12px",
  padding: "4px 8px",
  borderRadius: "999px",
  background: "#fed7aa",
  color: "#9a3412",
};

const stepDescStyle = {
  margin: 0,
  color: "#6b7280",
  lineHeight: "1.5",
};

const infoBoxStyle = {
  marginTop: "26px",
  padding: "16px",
  borderRadius: "18px",
  background: "#f5f3ff",
  color: "#4c1d95",
  lineHeight: "1.6",
};

const supportDividerStyle = {
  marginTop: "18px",
  paddingTop: "18px",
  borderTop: "1px solid rgba(124,58,237,0.15)",
};

const supportTitleStyle = {
  margin: "0 0 10px",
  color: "#4c1d95",
  fontSize: "18px",
};

const mailLinkStyle = {
  display: "block",
  color: "#6d28d9",
  textDecoration: "none",
  fontWeight: "700",
  marginBottom: "10px",
};

const phoneLinkStyle = {
  display: "block",
  color: "#16a34a",
  textDecoration: "none",
  fontWeight: "700",
};

export default TrackOrder;
